import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { finalize, from, Subscription, switchMap } from 'rxjs';
import { AuthService } from './auth.service';
import { CsrfService } from './csrf.service';

/**
 * Maintains a sliding authenticated session while the user is active and
 * terminates it after 30 minutes without browser or foreground HTTP activity.
 */
@Injectable({ providedIn: 'root' })
export class InactivityLogoutService {

  static readonly LOGOUT_REASON_KEY = 'kafaat.logoutReason';
  static readonly REASON_INACTIVITY = 'inactivity';

  private static readonly INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
  private static readonly RENEW_AFTER_MS = 10 * 60 * 1000;
  private static readonly ACTIVITY_THROTTLE_MS = 1000;

  private readonly activityEvents: ReadonlyArray<string> = [
    'mousemove',
    'mousedown',
    'keydown',
    'scroll',
    'touchstart',
    'wheel',
  ];

  private watching = false;
  private refreshing = false;
  private lastActivityAt = 0;
  private lastRefreshAt = 0;
  private lastRegisteredAt = 0;
  private activitySinceRefresh = false;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private renewalTimer: ReturnType<typeof setInterval> | null = null;
  private refreshSubscription: Subscription | null = null;

  private readonly boundActivity = (): void => this.registerActivity('dom');
  private readonly boundVisibilityChange = (): void => this.onVisibilityChange();

  constructor(private readonly injector: Injector) {}

  startWatching(): void {
    this.stopWatching();

    if (!this.authService.isLoggedIn()) {
      return;
    }

    const now = Date.now();
    this.watching = true;
    this.lastActivityAt = now;
    this.lastRefreshAt = now;
    this.lastRegisteredAt = now;
    this.activitySinceRefresh = false;

    this.activityEvents.forEach((event) =>
      window.addEventListener(event, this.boundActivity, { passive: true })
    );
    document.addEventListener('visibilitychange', this.boundVisibilityChange);

    this.scheduleInactivityTimeout();
    this.renewalTimer = setInterval(
      () => this.renewSessionIfNeeded(),
      InactivityLogoutService.RENEW_AFTER_MS
    );
  }

  stopWatching(): void {
    if (this.watching) {
      this.activityEvents.forEach((event) =>
        window.removeEventListener(event, this.boundActivity)
      );
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    }

    this.watching = false;
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = null;
    this.refreshing = false;
    this.clearTimers();
  }

  registerActivity(_source: 'dom' | 'http' = 'dom'): void {
    if (!this.watching) {
      return;
    }

    const now = Date.now();
    if (now - this.lastActivityAt >= InactivityLogoutService.INACTIVITY_TIMEOUT_MS) {
      this.onInactivityTimeout();
      return;
    }

    if (now - this.lastRegisteredAt < InactivityLogoutService.ACTIVITY_THROTTLE_MS) {
      return;
    }

    this.lastRegisteredAt = now;
    this.lastActivityAt = now;
    this.activitySinceRefresh = true;
    this.scheduleInactivityTimeout();
    this.renewSessionIfNeeded();
  }

  private onVisibilityChange(): void {
    if (!this.watching || document.hidden) {
      return;
    }

    this.registerActivity('dom');
  }

  private scheduleInactivityTimeout(): void {
    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer);
    }

    const remaining = Math.max(
      0,
      this.lastActivityAt + InactivityLogoutService.INACTIVITY_TIMEOUT_MS - Date.now()
    );
    this.inactivityTimer = setTimeout(() => this.onInactivityTimeout(), remaining);
  }

  private renewSessionIfNeeded(): void {
    if (
      !this.watching ||
      this.refreshing ||
      !this.activitySinceRefresh ||
      Date.now() - this.lastRefreshAt < InactivityLogoutService.RENEW_AFTER_MS
    ) {
      return;
    }

    if (Date.now() - this.lastActivityAt >= InactivityLogoutService.INACTIVITY_TIMEOUT_MS) {
      this.onInactivityTimeout();
      return;
    }

    this.refreshing = true;
    const refreshSubscription = this.authService.refreshAccessToken().pipe(
      switchMap(() => {
        this.csrfService.clearToken();
        return from(this.csrfService.fetchToken());
      }),
      finalize(() => {
        this.refreshing = false;
        this.refreshSubscription = null;
      })
    ).subscribe({
      next: () => {
        if (!this.watching) {
          return;
        }
        this.lastRefreshAt = Date.now();
        this.activitySinceRefresh = false;
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          this.authService.clearSessionState();
        }
      },
    });
    this.refreshSubscription = refreshSubscription.closed ? null : refreshSubscription;
  }

  private onInactivityTimeout(): void {
    if (!this.watching) {
      return;
    }

    if (Date.now() - this.lastActivityAt < InactivityLogoutService.INACTIVITY_TIMEOUT_MS) {
      this.scheduleInactivityTimeout();
      return;
    }

    this.stopWatching();
    sessionStorage.setItem(
      InactivityLogoutService.LOGOUT_REASON_KEY,
      InactivityLogoutService.REASON_INACTIVITY
    );
    this.authService.logout();
  }

  private clearTimers(): void {
    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.renewalTimer !== null) {
      clearInterval(this.renewalTimer);
      this.renewalTimer = null;
    }
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get csrfService(): CsrfService {
    return this.injector.get(CsrfService);
  }
}
